import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { webApi, errText, fmtDate } from "@/lib/webApi";
import { PageHead, primary, ghost, useMe } from "@/pages/web/WebApp";
import { StorageBar } from "@/pages/web/WebMatters";
import { Card, Eyebrow, Pill } from "@/pages/web/ui";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const HOW = { auto_renew: "Auto-renew", one_time: "One-time payment", manual: "Set by the Dicta team", free: "Free", complimentary: "No limits, no charges. Set by the Dicta team" };

const payTone = (s) => (["paid", "success", "captured"].includes(s) ? "ok" : s === "failed" ? "danger" : "neutral");

function ConfirmDialog({ open, onOpenChange, title, text, action, onConfirm }) {
  const confirmRef = useRef(null);
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="theme-warm" onOpenAutoFocus={(e) => { e.preventDefault(); confirmRef.current?.focus(); }}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{text}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction ref={confirmRef} onClick={onConfirm} className="bg-[--w-danger] text-white hover:bg-red-700">{action}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function Billing() {
  const [b, setB] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const load = useCallback(() => webApi.get("/billing").then((r) => setB(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!b) return null;
  const pay = async (opt) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await webApi.post("/billing/link", { opt });
      if (w) w.location = data.url; else window.location = data.url;
    } catch (e) { if (w) w.close(); toast.error(errText(e)); }
  };
  const cancel = async () => {
    const { data } = await webApi.post("/billing/cancel");
    toast.message(data.message.split("\n")[0]);
    load();
  };
  const update = async () => {
    try { const { data } = await webApi.post("/billing/update-payment"); window.open(data.url, "_blank"); } catch (e) { toast.error(errText(e)); }
  };
  const end = b.manual_until || b.paid_until;
  const pill = b.status === "retrying" ? ["Payment retrying", "warn"]
    : b.cancel_requested && end ? [`Ends on ${fmtDate(end)}`, "warn"]
    : end && b.how === "auto_renew" ? [`Renews on ${fmtDate(end)}`, "ok"]
    : end ? [`Ends on ${fmtDate(end)}`, "ok"]
    : ["Active", "ok"];
  return (
    <div data-testid="web-billing">
      <PageHead title="Plan & billing" />
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-5">
          <Eyebrow>Current plan</Eyebrow>
          <p className="mt-1 text-2xl font-semibold text-[--w-text]" data-testid="billing-plan">{b.plan_label}</p>
          {b.how !== "free" && <p className="mt-1 text-sm text-[--w-text-2]" data-testid="billing-how">{HOW[b.how]}{b.status && b.how !== "complimentary" ? ` · ${b.cancel_requested && b.status === "active" ? "cancelled" : b.status.replace("_", " ")}` : ""}</p>}
          <Pill tone={pill[1]} className="mt-2">{pill[0]}</Pill>
          <div className="mt-4"><StorageBar storage={b.storage} /></div>
          <div className="mt-4 flex flex-wrap gap-2">
            {b.how === "auto_renew" && !b.cancel_requested && b.how !== "complimentary" && <button onClick={() => setCancelOpen(true)} className={ghost} data-testid="billing-cancel-button">Cancel auto-renewal</button>}
            {b.status === "retrying" && <button onClick={update} className={primary} data-testid="billing-update-payment-button">Update payment</button>}
          </div>
        </Card>
        {b.options.length > 0 && <Card className="p-5">
          <Eyebrow className="mb-3">Change plan or pay once (prices include taxes)</Eyebrow>
          <div className="grid gap-2 sm:grid-cols-2">
            {b.options.map((o) => (
              <button key={o.id} onClick={() => pay(o.id)} data-testid={`billing-option-${o.id}`}
                className="rounded-md border border-[--w-border] p-3 text-left transition-colors duration-200 hover:border-[--w-accent] hover:bg-blue-50">
                <p className="text-sm font-medium text-[--w-text]">{o.title}</p>
                <p className="text-xs text-[--w-text-3]">{o.desc}</p>
              </button>
            ))}
          </div>
        </Card>}
      </div>
      <h2 className="mb-3 mt-8 text-base font-semibold text-[--w-text] md:text-lg">Payment history</h2>
      <Card className="overflow-auto" data-testid="billing-history">
        <table className="w-full text-sm">
          <thead className="bg-[--w-card-2] text-left text-[11px] uppercase tracking-wide text-[--w-text-3]">
            <tr><th className="px-4 py-2">Plan</th><th className="px-4 py-2">Amount</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Date</th></tr>
          </thead>
          <tbody>
            {b.payments.length === 0 && <tr><td colSpan={4} className="px-4 py-3 text-sm text-[--w-text-3]">No payments yet.</td></tr>}
            {b.payments.map((p) => (
              <tr key={p.id} className="border-t border-[--w-border]">
                <td className="px-4 py-2 text-[--w-text]">{(b.options.find((o) => o.id === p.opt) || {}).title || p.plan}</td>
                <td className="px-4 py-2 font-mono-data tabular-nums">₹{((p.amount || 0) / 100).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2"><Pill tone={payTone(p.status)}>{p.status}</Pill></td>
                <td className="px-4 py-2 text-[13px] text-[--w-text-2] tabular-nums">{fmtDate(p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <p className="mt-6 flex flex-wrap gap-4 text-sm text-[--w-text-2]">
        {Object.entries(b.links).map(([k, url]) => <a key={k} href={url} target="_blank" rel="noreferrer" className="capitalize hover:text-[--w-text]" data-testid={`billing-link-${k}`}>{k === "refunds" ? "Cancellation & Refunds" : k}</a>)}
      </p>
      <ConfirmDialog open={cancelOpen} onOpenChange={setCancelOpen} title="Cancel auto-renewal" text="Stop auto-renewal? Your plan stays on until the end of the paid period." action="Cancel auto-renewal" onConfirm={cancel} />
    </div>
  );
}

export function Account() {
  const nav = useNavigate();
  const { reload } = useMe();
  const [a, setA] = useState(null);
  const [form, setForm] = useState({ name: "", advocate_name: "" });
  const [delOpen, setDelOpen] = useState(false);
  const load = useCallback(() => webApi.get("/account").then((r) => { setA(r.data); setForm({ name: r.data.name || "", advocate_name: r.data.advocate_name || "" }); }), []);
  useEffect(() => { load(); }, [load]);
  if (!a) return null;
  const save = async () => { await webApi.patch("/account", form); toast.success("Saved"); reload(); };
  const signOut = async (all) => {
    await webApi.post(all ? "/auth/signout-all" : "/auth/signout");
    nav("/app/login", { replace: true });
  };
  const exportData = async () => {
    try { const { data } = await webApi.post("/account/export"); window.open(data.url, "_blank"); } catch (e) { toast.error(errText(e)); }
  };
  const del = async () => {
    try { const { data } = await webApi.post("/account/delete"); toast.message(data.message); } catch (e) { toast.error(errText(e)); }
  };
  const field = "mt-1 w-full rounded-md border border-[--w-border] bg-[--w-card] px-3 py-2 text-sm text-[--w-text] outline-none focus:border-[--w-accent]";
  const label = "block text-[13px] font-medium text-[--w-text-2]";
  return (
    <div data-testid="web-account">
      <PageHead title="Account" />
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="space-y-3 p-5">
          <label className={label}>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="account-name-input" /></label>
          <label className={label}>Advocate name<input value={form.advocate_name} onChange={(e) => setForm({ ...form, advocate_name: e.target.value })} className={field} data-testid="account-advocate-input" /></label>
          <button onClick={save} className={primary} data-testid="account-save-button">Save</button>
        </Card>
        <Card className="p-5">
          <Eyebrow>WhatsApp numbers</Eyebrow>
          {a.numbers.map((n, i) => <p key={i} className="mt-2 text-sm text-[--w-text]" data-testid={`account-number-${i}`}><span className="font-mono-data">{n.masked}</span>{n.current && <Pill tone="ok" className="ml-2 px-2">signed in</Pill>}</p>)}
          <p className="mt-2 text-xs text-[--w-text-3]">Link or unlink numbers on WhatsApp.</p>
        </Card>
      </div>
      <Card className="mt-4 p-5" data-testid="account-sessions">
        <Eyebrow className="mb-2">Active sessions</Eyebrow>
        {a.sessions.map((s) => (
          <div key={s.id} className="flex items-center justify-between border-t border-[--w-border] py-2 text-sm first:border-0">
            <span className="text-[--w-text]">{s.device} <span className="text-[--w-text-3]">· last used {fmtDate(s.last_used)}</span></span>
            {s.current && <Pill>this browser</Pill>}
          </div>
        ))}
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => signOut(false)} className={ghost} data-testid="sign-out-button"><LogOut className="h-4 w-4" /> Sign out</button>
          <button onClick={() => signOut(true)} className={ghost} data-testid="sign-out-everywhere-button"><LogOut className="h-4 w-4" /> Sign out everywhere</button>
        </div>
      </Card>
      <Card className="mt-4 p-5">
        <Eyebrow className="mb-3">Your data</Eyebrow>
        <div className="flex flex-wrap gap-2">
          <button onClick={exportData} className={ghost} data-testid="account-export-button"><Download className="h-4 w-4" /> MY DATA export</button>
          <button onClick={() => setDelOpen(true)} className={`${ghost} text-red-700`} data-testid="account-delete-button"><Trash2 className="h-4 w-4" /> Delete my data</button>
        </div>
      </Card>
      <ConfirmDialog open={delOpen} onOpenChange={setDelOpen} title="Delete my data" text="Start deleting all your Dicta data? You'll confirm on WhatsApp." action="Delete my data" onConfirm={del} />
    </div>
  );
}
